import { useEffect, useMemo, useState, useRef, forwardRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Check, Sparkles, Rocket, ShieldCheck, RefreshCw, ArrowRight, Clock, CreditCard, Building2, Copy, Send, AlertCircle, Landmark, Ticket } from "lucide-react";
import DashboardShell from "@/components/hola/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

export default function BillingPage() {
  return (
    <DashboardShell active="billing">
      {({ merchant, setMerchant }) => <BillingBody merchant={merchant} setMerchant={setMerchant} />}
    </DashboardShell>
  );
}

function BillingBody({ merchant, setMerchant }) {
  const [params, setParams] = useSearchParams();
  const [plan, setPlan] = useState(null);
  const [sub, setSub] = useState(merchant.subscription || null);
  const [loadingSub, setLoadingSub] = useState(true);
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [pollStatus, setPollStatus] = useState(null); // {stage, message}
  const navigate = useNavigate();

  const sessionId = params.get("session_id");
  const canceled = params.get("canceled") === "1";
  const upgradeFocus = params.get("upgrade") === "full";

  const loadBilling = async () => {
    try {
      const { data } = await api.get("/billing/me");
      setSub(data.subscription);
      setPlan(data.plan);
    } catch (e) { console.warn("silent error:", e && e.message || e); } finally {
      setLoadingSub(false);
    }
  };

  useEffect(() => {
    loadBilling();
  }, []);

  useEffect(() => {
    if (canceled) {
      toast.info("Pago cancelado. Puedes intentarlo cuando quieras.");
      setParams({}, { replace: true });
    }
  }, [canceled, setParams]);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let stopped = false;
    setPollStatus({ stage: "checking", message: "Confirmando tu pago con Stripe..." });

    const poll = async () => {
      if (stopped) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPollStatus({ stage: "paid", message: "¡Suscripción activada!" });
          setSub(data.subscription);
          // Refresh /auth/me so the shell picks up the new subscription in the merchant object
          try {
            const me = await api.get("/auth/me");
            setMerchant?.(me.data);
          } catch (e) { console.warn("silent error:", e && e.message || e); }
          toast.success("¡Pago confirmado! Tu suscripción está activa.");
          setTimeout(() => setParams({}, { replace: true }), 800);
          return;
        }
        if (data.status === "expired") {
          setPollStatus({ stage: "expired", message: "La sesión de pago venció." });
          toast.error("La sesión de pago venció. Intenta de nuevo.");
          return;
        }
        if (attempts >= 8) {
          setPollStatus({ stage: "timeout", message: "El pago aún no se confirma. Refresca esta página en un minuto." });
          return;
        }
        setPollStatus({ stage: "checking", message: `Procesando pago... (${attempts}/8)` });
        setTimeout(poll, 2500);
      } catch (e) {
        setPollStatus({ stage: "error", message: "Error consultando el estado del pago." });
      }
    };
    poll();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const subscribe = async (planId = "pro_monthly") => {
    setCreatingCheckout(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo iniciar el pago");
      setCreatingCheckout(false);
    }
  };

  const active = sub?.active;
  const isTrial = sub?.is_trial;
  const status = sub?.status || "expired";
  const daysLeft = sub?.days_remaining ?? 0;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <p className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Suscripción</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Plan & facturación</h1>
        <p className="text-sm text-neutral-500 mt-1">Mantén tu radar activo. Los comercios sin suscripción no aparecen en el radar de los transeúntes.</p>
      </div>

      {/* Polling banner */}
      {pollStatus && (
        <div className={`rounded-2xl p-4 flex items-center gap-3 text-sm font-medium border ${
          pollStatus.stage === "paid" ? "bg-emerald-50 border-emerald-200 text-emerald-900" :
          pollStatus.stage === "expired" || pollStatus.stage === "error" ? "bg-red-50 border-red-200 text-red-900" :
          pollStatus.stage === "timeout" ? "bg-amber-50 border-amber-200 text-amber-900" :
          "bg-blue-50 border-blue-200 text-blue-900"
        }`} data-testid="billing-poll-banner">
          {pollStatus.stage === "checking" && <RefreshCw className="w-4 h-4 animate-spin" />}
          {pollStatus.stage === "paid" && <Check className="w-4 h-4" />}
          <span>{pollStatus.message}</span>
        </div>
      )}

      {/* Current subscription card */}
      <div className={`rounded-3xl border p-6 ${
        active ? "bg-gradient-to-br from-[#0055FF] to-[#3d7bff] text-white border-transparent" :
        "bg-white border-neutral-200"
      }`} data-testid="billing-status-card">
        {loadingSub ? (
          <div className="text-sm opacity-70">Cargando estado...</div>
        ) : (
          <>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold rounded-full px-2.5 py-1 ${
                  active ? "bg-white/20 text-white" : "bg-red-50 text-red-700 border border-red-200"
                }`}>
                  {status === "trialing" && "🎁 Trial gratuito"}
                  {status === "active" && "✅ Suscripción activa"}
                  {status === "expired" && "⚠️ Suscripción expirada"}
                </div>
                <div className="font-heading text-2xl md:text-3xl font-extrabold mt-2">
                  {isTrial ? `Prueba gratis · ${daysLeft} día${daysLeft === 1 ? "" : "s"} restantes` :
                   active ? `Renueva en ${daysLeft} día${daysLeft === 1 ? "" : "s"}` :
                   "Tu radar está pausado"}
                </div>
                {sub?.active_until && (
                  <div className={`text-sm mt-1 ${active ? "text-white/80" : "text-neutral-500"}`}>
                    <Clock className="inline w-3.5 h-3.5 mr-1" />
                    Válida hasta {new Date(sub.active_until).toLocaleDateString("es", { day: "2-digit", month: "long", year: "numeric" })}
                  </div>
                )}
              </div>
              {!active && (
                <Button onClick={subscribe} disabled={creatingCheckout} className="rounded-full bg-[#FF007A] hover:bg-[#e5006e] text-white font-bold h-12 px-6" data-testid="billing-subscribe-btn">
                  <CreditCard className="w-4 h-4" /> {creatingCheckout ? "Abriendo..." : "Activar mi radar"}
                </Button>
              )}
              {active && (
                <Button onClick={subscribe} disabled={creatingCheckout} variant="outline" className="rounded-full font-semibold h-11 border-white/40 text-white hover:bg-white/10" data-testid="billing-renew-btn">
                  <RefreshCw className="w-4 h-4" /> {isTrial ? "Suscribirme ahora" : "Renovar 30 días"}
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Paquetes */}
      <PackagesSection sub={sub} active={active} subscribe={subscribe} creating={creatingCheckout} upgradeFocus={upgradeFocus} />

      {/* Reporta tu pago (auto-activa 30 días) */}
      <ReportPaymentSection onActivated={loadBilling} />

      <p className="text-xs text-neutral-400">
        Modelo de facturación: pago único cada 30 días — no hay cobros automáticos sorpresa. Cuando se venzan los 30 días, tu radar pausa automáticamente hasta que renueves.
      </p>

      {/* Transferencia bancaria (MXN) */}
      <BankTransferSection />
    </div>
  );
}


function ReportPaymentSection({ onActivated }) {
  const [tab, setTab] = useState("transfer");
  const [form, setForm] = useState({ reference: "", payer_name: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.reference.trim()) {
      toast.error("Ingresa la referencia o folio del pago");
      return;
    }
    setSaving(true);
    try {
      let res;
      if (tab === "transfer") {
        res = await api.post("/billing/bank/submit", {
          reference: form.reference,
          depositor_name: form.payer_name || "N/A",
          transferred_at: new Date().toISOString().slice(0, 10),
        });
      } else {
        res = await api.post("/billing/report-payment", {
          method: tab === "stripe" ? "stripe_card" : "oxxo",
          reference: form.reference,
          payer_name: form.payer_name,
        });
      }
      toast.success(res.data.message || "¡Cuenta activada!");
      setForm({ reference: "", payer_name: "" });
      onActivated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al reportar");
    } finally {
      setSaving(false);
    }
  };

  const TABS = [
    { k: "transfer", label: "Transferencia SPEI", icon: Landmark, color: "emerald", placeholder: "Folio SPEI 0123456789" },
    { k: "stripe", label: "Pago con Tarjeta (Stripe)", icon: CreditCard, color: "blue", placeholder: "ID de la transacción Stripe (pi_xxx / cs_xxx)" },
    { k: "oxxo", label: "Pago en OXXO", icon: Building2, color: "red", placeholder: "Número de referencia OXXO del voucher" },
  ];
  const active = TABS.find((t) => t.k === tab);

  return (
    <div className="rounded-3xl bg-white border-2 border-[#FF007A]/30 p-6" data-testid="report-payment-section">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-9 h-9 rounded-xl bg-[#FF007A] text-white grid place-items-center"><Send className="w-4 h-4" /></div>
        <div>
          <div className="font-heading font-extrabold text-xl">Reporta tu pago</div>
          <div className="text-xs text-neutral-500">Al enviar → tu cuenta se activa 30 días automáticamente</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        {TABS.map((t) => (
          <button
            key={t.k}
            onClick={() => { setTab(t.k); setForm({ reference: "", payer_name: "" }); }}
            data-testid={`report-tab-${t.k}`}
            className={`px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 transition-colors ${
              tab === t.k ? "bg-black text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5 grid md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Label>Referencia / folio del pago *</Label>
          <Input
            value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })}
            placeholder={active.placeholder}
            className="mt-1.5 h-12 rounded-xl"
            data-testid={`report-reference-${tab}`}
          />
        </div>
        <div>
          <Label>Nombre de quien pagó (opcional)</Label>
          <Input
            value={form.payer_name}
            onChange={(e) => setForm({ ...form, payer_name: e.target.value })}
            placeholder="Ej: María López"
            className="mt-1.5 h-11 rounded-xl"
            data-testid={`report-payer-${tab}`}
          />
        </div>
      </div>

      <Button
        onClick={submit}
        disabled={saving}
        className="mt-5 h-12 rounded-full bg-[#FF007A] hover:bg-[#e5006e] text-white font-bold px-8"
        data-testid={`report-submit-${tab}`}
      >
        {saving ? "Enviando..." : "Enviar y activar mi cuenta"} <ArrowRight className="w-4 h-4" />
      </Button>

      <div className="mt-3 text-xs text-neutral-500 flex items-start gap-1.5">
        <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>Al enviar aceptas que tu pago aún no ha sido verificado. HOLA RADAR revisará y podrá desactivar tu cuenta si el pago no se recibió realmente.</span>
      </div>
    </div>
  );
}


function BankTransferSection() {
  const [details, setDetails] = useState(null);
  const [expand, setExpand] = useState(false);
  const [form, setForm] = useState({
    reference: "",
    depositor_name: "",
    transferred_at: new Date().toISOString().slice(0, 10),
    note: "",
  });
  const [saving, setSaving] = useState(false);
  const [transfers, setTransfers] = useState([]);

  const load = async () => {
    try {
      const [d, m] = await Promise.all([
        api.get("/billing/bank/details"),
        api.get("/billing/bank/mine"),
      ]);
      setDetails(d.data);
      setTransfers(m.data.transfers || []);
    } catch (e) { console.warn("silent error:", e && e.message || e); }
  };
  useEffect(() => { load(); }, []);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.reference || !form.depositor_name) {
      toast.error("Faltan datos de la transferencia");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/billing/bank/submit", form);
      toast.success(data.message || "Recibido");
      setForm({ reference: "", depositor_name: "", transferred_at: new Date().toISOString().slice(0, 10), note: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar");
    } finally {
      setSaving(false);
    }
  };

  if (!details) return null;

  const clabeFmt = details.clabe.replace(/(\d{3})(\d{3})(\d{12})/, "$1 $2 $3");

  return (
    <div className="rounded-3xl bg-white border border-neutral-200 p-6" data-testid="bank-transfer-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 grid place-items-center">
            <Landmark className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading font-extrabold text-xl">Pagar por transferencia bancaria</div>
            <div className="text-xs text-neutral-500">SPEI a BBVA México · Alternativa sin tarjeta</div>
          </div>
        </div>
        <Button
          onClick={() => setExpand((v) => !v)}
          variant="outline"
          className="rounded-full font-semibold"
          data-testid="bank-toggle-btn"
        >
          {expand ? "Ocultar" : "Ver datos y depositar"}
        </Button>
      </div>

      {expand && (
        <div className="mt-6 space-y-4">
          <div className="rounded-2xl bg-[#FAFAFA] border border-neutral-200 p-5">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold mb-3">Datos del depósito</div>
            <BankRow label="Beneficiario" value={details.holder} onCopy={() => copy(details.holder)} />
            <BankRow label="Banco" value={details.bank} onCopy={() => copy(details.bank)} />
            <BankRow label="CLABE Interbancaria" value={clabeFmt} onCopy={() => copy(details.clabe)} mono testid="bank-clabe" />
            <BankRow label="Concepto" value={details.concept} onCopy={() => copy(details.concept)} />
            <BankRow label="Monto" value={`$${details.amount_mxn.toFixed(0)} MXN`} onCopy={() => copy(String(details.amount_mxn))} highlight />
          </div>

          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
            <ol className="text-sm text-amber-900 list-decimal list-inside space-y-1">
              {details.instructions.map((s) => (<li key={s}>{s}</li>))}
            </ol>
          </div>

          <form onSubmit={submit} className="rounded-2xl bg-white border border-neutral-200 p-5 space-y-3">
            <div className="font-heading font-extrabold flex items-center gap-2 mb-1">
              <Send className="w-4 h-4 text-[#0055FF]" /> Reporta tu depósito
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Nombre de quien depositó *</Label>
                <Input
                  value={form.depositor_name}
                  onChange={(e) => setForm({ ...form, depositor_name: e.target.value })}
                  placeholder="María López"
                  className="mt-1 h-11 rounded-xl"
                  data-testid="bank-depositor-input"
                  required
                />
              </div>
              <div>
                <Label>Referencia / folio del banco *</Label>
                <Input
                  value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  placeholder="Folio SPEI o número de operación"
                  className="mt-1 h-11 rounded-xl"
                  data-testid="bank-reference-input"
                  required
                />
              </div>
              <div>
                <Label>Fecha del depósito</Label>
                <Input
                  type="date"
                  value={form.transferred_at}
                  onChange={(e) => setForm({ ...form, transferred_at: e.target.value })}
                  className="mt-1 h-11 rounded-xl"
                  data-testid="bank-date-input"
                />
              </div>
              <div>
                <Label>Nota adicional (opcional)</Label>
                <Input
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Ej: pago tercer local"
                  className="mt-1 h-11 rounded-xl"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-12 px-6"
              data-testid="bank-submit-btn"
            >
              {saving ? "Enviando..." : (<>Enviar comprobante <ArrowRight className="w-4 h-4" /></>)}
            </Button>
          </form>

          {transfers.length > 0 && (
            <div className="rounded-2xl bg-white border border-neutral-200 overflow-hidden">
              <div className="px-5 py-3 text-xs uppercase tracking-widest text-neutral-500 font-bold border-b border-neutral-100">
                Mis depósitos reportados
              </div>
              <div className="divide-y divide-neutral-100">
                {transfers.map((t) => (
                  <div key={t.transfer_id} className="px-5 py-3 flex items-center gap-3 text-sm" data-testid={`bank-row-${t.transfer_id}`}>
                    <div className="flex-1">
                      <div className="font-bold">{t.reference}</div>
                      <div className="text-xs text-neutral-500">{t.depositor_name} · {new Date(t.created_at).toLocaleString("es")}</div>
                    </div>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                      t.status === "approved" ? "bg-emerald-50 text-emerald-700" :
                      t.status === "rejected" ? "bg-red-50 text-red-700" :
                      "bg-amber-50 text-amber-700"
                    }`}>
                      {t.status === "approved" && "✅ Aprobado"}
                      {t.status === "rejected" && "❌ Rechazado"}
                      {t.status === "pending" && "⏳ En revisión"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BankRow({ label, value, onCopy, mono, highlight, testid }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-neutral-200/60 last:border-0">
      <div className="text-xs text-neutral-500 font-medium w-40">{label}</div>
      <div className={`flex-1 ${mono ? "font-mono" : ""} ${highlight ? "font-heading font-black text-2xl text-emerald-600" : "font-bold"}`} data-testid={testid}>{value}</div>
      <button
        onClick={onCopy}
        className="w-8 h-8 rounded-full hover:bg-neutral-100 grid place-items-center text-neutral-500"
        title="Copiar"
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function PlanFeature({ text }) {
  return (
    <li className="flex items-start gap-2">
      <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
      <span>{text}</span>
    </li>
  );
}


const PKG_FEATURES = {
  cupones: ["QR personalizado + póster imprimible", "Base de datos propia de clientes", "Cupones con diseño IA (6/mes)", "Campañas por segmento con nombre del cliente", "Campañas a TODA tu población (base global HOLA RADAR)", "Entrega boletos del Sorteo Mensual de electrodomésticos (QR propio, premios pagados por nosotros)", "Canje en caja con medición de ventas", "Avisos push y recordatorio de vencimiento", "Cuponera del cliente + concurso Cupón de Oro"],
  full: ["TODO el Paquete Cupones Marketing", "Radar GPS 50m · hasta 1,400 matches/mes", "Sistema de Ventas Referidas: promotores venden por ti y pagas comisión solo al vender", "Ofertas y regalos ilimitados", "Aura: guía virtual IA (voz y texto) + 50 min gratis/mes", "Diseño IA de ofertas y catálogo (8/mes)", "MANADA descuentos grupales", "Atínale al Precio (gamificación)", "Catálogo visual 10 fotos + IA vendedora"],
};

function PackagesSection({ sub, active, subscribe, creating, upgradeFocus }) {
  const tier = sub?.tier || "full";
  const current = active ? tier : null;
  const fullRef = useRef(null);
  useEffect(() => {
    if (upgradeFocus && fullRef.current) fullRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [upgradeFocus]);
  return (
    <div className="space-y-4" data-testid="billing-packages">
      <div>
        <h2 className="font-heading font-extrabold text-2xl">{upgradeFocus ? "Contrata el PLAN COMPLETO" : "Elige tu paquete"}</h2>
        <p className="text-sm text-neutral-500">{upgradeFocus ? "Activa radar GPS, Aura IA, ofertas, diseño IA, MANADA, Atínale y catálogo — conservas todo tu sistema de cupones." : "Pago único cada 30 días · sin cobros automáticos · cambia de paquete cuando quieras."}</p>
      </div>
      <div className="grid md:grid-cols-2 gap-5 items-stretch">
        <PackageCard
          testid="pkg-cupones" name="PAQUETE CUPONES MARKETING" price="$299" total="$346.84" usd="18" accent="#FF007A" icon={Ticket}
          tagline="Tu base de datos, cupones a toda tu población y boletos del sorteo" features={PKG_FEATURES.cupones}
          isCurrent={current === "cupones"} creating={creating} dim={upgradeFocus}
          onMxn={() => subscribe("cupones_monthly_mxn")} onUsd={() => subscribe("cupones_monthly")}
        />
        <PackageCard
          ref={fullRef}
          testid="pkg-full" name="PAQUETE HOLA RADAR" price="$990" total="$1,148.40" usd="59" accent="#0055FF" icon={Rocket} highlight focus={upgradeFocus}
          tagline="Toda la plataforma: radar GPS, Aura IA, cupones y gamificación" features={PKG_FEATURES.full}
          isCurrent={current === "full" && !sub?.is_trial} creating={creating}
          onMxn={() => subscribe("pro_monthly_mxn")} onUsd={() => subscribe("pro_monthly")}
        />
      </div>
      {sub?.is_trial && <p className="text-xs text-[#0055FF] font-semibold" data-testid="billing-trial-note">Estás en periodo de prueba con TODO incluido. Al terminar, elige el paquete que quieras conservar.</p>}
    </div>
  );
}

const PackageCard = forwardRef(function PackageCard({ testid, name, price, total, usd, accent, icon: Icon, tagline, features, isCurrent, creating, onMxn, onUsd, highlight, focus, dim }, ref) {
  return (
    <div ref={ref} className={`rounded-3xl bg-white border-2 p-6 flex flex-col relative transition-all ${isCurrent ? "border-emerald-400" : highlight ? "border-[#0055FF]/40" : "border-neutral-200"} ${focus ? "ring-4 ring-[#FF007A]/40 shadow-2xl shadow-[#0055FF]/20 scale-[1.01]" : ""} ${dim ? "opacity-60" : ""}`} data-testid={testid} data-focus={focus ? "true" : undefined}>
      {isCurrent && <span className="absolute -top-3 left-6 rounded-full bg-emerald-500 text-white text-[10px] font-extrabold uppercase tracking-widest px-3 py-1" data-testid={`${testid}-current`}>Tu paquete actual</span>}
      {highlight && !isCurrent && <span className="absolute -top-3 left-6 rounded-full text-white text-[10px] font-extrabold uppercase tracking-widest px-3 py-1" style={{ background: accent }}>{focus ? "Plan completo recomendado" : "Más completo"}</span>}
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-xl grid place-items-center text-white" style={{ background: accent }}><Icon className="w-5 h-5" /></div>
        <div><div className="font-heading font-extrabold text-lg leading-tight">{name}</div><div className="text-xs text-neutral-500">{tagline}</div></div>
      </div>
      <div className="mt-5 flex items-baseline gap-1"><span className="font-heading font-black text-5xl">{price}</span><span className="text-neutral-500 font-semibold">MXN + IVA / mes</span></div>
      <div className="text-xs text-neutral-500 mt-1">Total con IVA: <strong>{total} MXN</strong> · o ${usd} USD con tarjeta internacional</div>
      <ul className="mt-5 space-y-2 text-sm flex-1">{features.map((f) => <PlanFeature key={f} text={f} />)}</ul>
      <div className="mt-6 flex flex-col gap-2">
        <Button onClick={onMxn} disabled={creating} className="rounded-full text-white font-bold h-12" style={{ background: accent }} data-testid={`${testid}-pay-mxn`}>
          {creating ? "Abriendo Stripe..." : isCurrent ? "Renovar 30 días" : `Contratar · ${total} MXN`} <ArrowRight className="w-4 h-4" />
        </Button>
        <Button onClick={onUsd} disabled={creating} variant="outline" className="rounded-full font-semibold h-10 text-xs" data-testid={`${testid}-pay-usd`}>Pagar ${usd} USD (tarjeta internacional)</Button>
      </div>
    </div>
  );
});
